import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "./utils";

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  /** Heading rendered in the card header. */
  title?: ReactNode;
  /** Secondary line under the title. */
  subtitle?: ReactNode;
  /** Controls placed at the right of the header (buttons, badges). */
  actions?: ReactNode;
  /** Inner padding scale. @default "md" */
  padding?: "none" | "sm" | "md" | "lg";
  /** Use the elevated design-system panel (`gz-panel--raised`). */
  raised?: boolean;
  /** Adds hover elevation + pointer cursor (use with onClick/role). */
  interactive?: boolean;
  /** Footer region below the body. */
  footer?: ReactNode;
  children?: ReactNode;
}

/** Design-system panel (`gz-panel`) with an optional header/footer. */
export function Card({
  title,
  subtitle,
  actions,
  padding = "md",
  raised = false,
  interactive = false,
  footer,
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <section
      className={cx(
        "gz-panel",
        raised && "gz-panel--raised",
        "xz-card",
        `xz-card--pad-${padding}`,
        interactive && "xz-card--interactive",
        className,
      )}
      {...rest}
    >
      {(title != null || actions != null) && (
        <header className="xz-card__head">
          <div className="xz-card__titles">
            {title != null && <h3 className="xz-card__title gz-type-heading">{title}</h3>}
            {subtitle != null && <p className="xz-card__subtitle gz-muted">{subtitle}</p>}
          </div>
          {actions != null && <div className="xz-card__actions">{actions}</div>}
        </header>
      )}
      {children != null && <div className="xz-card__body">{children}</div>}
      {footer != null && <footer className="xz-card__foot">{footer}</footer>}
    </section>
  );
}
